/**
 * SystemPromptEditor
 * -------------------
 *
 * September 2026
 *
 * Lets the user rewrite the instructions sent ahead of every narrative/chat
 * request. Saved in this browser only; DOEAnalyzerPage and NarrativeChat read
 * it from localStorage when they call the backend.
 */
import * as React from "react";
import axios from "axios";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";

export const SYSTEM_PROMPT_OVERRIDE_KEY = "doeAnalyzer.systemPromptOverride";

function SystemPromptEditor() {
  const [config, setConfig] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState("");
  const [savedText, setSavedText] = React.useState("");
  const [justSaved, setJustSaved] = React.useState(false);

  React.useEffect(() => {
    axios
      .get("/api/doe/narrative/config")
      .then((res) => setConfig(res.data))
      .catch(() => setConfig({}));
  }, []);

  React.useEffect(() => {
    if (!config) return;
    let stored = null;
    try {
      stored = window.localStorage.getItem(SYSTEM_PROMPT_OVERRIDE_KEY);
    } catch {
      // Ignore -- a private window or blocked storage just means no saved override.
    }
    const initial = stored || config.default_instructions || "";
    setText(initial);
    setSavedText(stored || "");
  }, [config]);

  if (!config) return null;

  const save = () => {
    try {
      window.localStorage.setItem(SYSTEM_PROMPT_OVERRIDE_KEY, text);
    } catch {
      // Best-effort only.
    }
    setSavedText(text);
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 2000);
  };

  const resetToDefault = () => {
    try {
      window.localStorage.removeItem(SYSTEM_PROMPT_OVERRIDE_KEY);
    } catch {
      // Best-effort only.
    }
    setText(config.default_instructions || "");
    setSavedText("");
  };

  const dirty = text !== savedText;
  const usingOverride = savedText.trim().length > 0;

  return (
    <div className="doe-column" style={{ marginTop: "0.75rem" }}>
      <Button
        label={open ? "Hide system prompt editor" : "Edit system prompt (advanced)"}
        icon="pi pi-cog"
        link
        size="small"
        onClick={() => setOpen((v) => !v)}
        style={{ alignSelf: "flex-start" }}
      />
      {usingOverride && !open && (
        <small className="doe-muted">A custom system prompt is currently in use for this browser.</small>
      )}
      {open && (
        <div className="doe-column">
          <Message
            severity="warn"
            text="Only do this if you know what you are doing. This replaces the instructions sent to the AI model for every narrative and follow-up question."
          />
          <InputTextarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={14}
            style={{ fontFamily: "monospace", fontSize: "0.85rem", width: "100%" }}
          />
          <div className="doe-row">
            <Button label={justSaved ? "Saved" : "Save"} icon={justSaved ? "pi pi-check" : "pi pi-save"} onClick={save} disabled={!dirty && !justSaved} />
            <Button label="Reset to default" outlined size="small" onClick={resetToDefault} disabled={!usingOverride && text === (config.default_instructions || "")} />
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemPromptEditor;
